import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  useMemo,
  useState,
} from "react";

import {
  Controller,
  useForm,
} from "react-hook-form";

import { SoftDataGrid } from "../../../components/data/SoftDataGrid";
import { getRoles } from "../../../services/rolesService";
import { getShops } from "../../../services/shopsService";

import {
  assignUserRole,
  assignUserShops,
  createUser,
  deleteUser,
  getUsers,
  updateUser,
} from "../../../services/usersService";

import { getApiErrorMessage } from "../../../utils/apiData";
import { UserAdminActions } from "../components/UserAdminActions";

function extractUsers(payload) {
  const paginator =
    payload?.users ??
    payload?.data?.users ??
    payload?.data ??
    payload;

  if (Array.isArray(paginator?.data)) {
    return paginator.data;
  }

  if (Array.isArray(paginator)) {
    return paginator;
  }

  return [];
}

function extractRoles(payload) {
  const paginator =
    payload?.roles ??
    payload?.data?.roles ??
    payload?.data ??
    payload;

  if (Array.isArray(paginator?.data)) {
    return paginator.data;
  }

  if (Array.isArray(paginator)) {
    return paginator;
  }

  return [];
}

function extractShops(payload) {
  const paginator =
    payload?.shops ??
    payload?.data?.shops ??
    payload?.data ??
    payload;

  if (Array.isArray(paginator?.data)) {
    return paginator.data;
  }

  if (Array.isArray(paginator)) {
    return paginator;
  }

  return [];
}

function initials(name) {
  return String(name || "U")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

const fieldSx = {
  "& .MuiInputBase-root": {
    minHeight: 38,
    borderRadius: 0,
    fontSize: "0.72rem",
  },

  "& .MuiInputLabel-root": {
    fontSize: "0.72rem",
  },

  "& .MuiFormHelperText-root": {
    mx: 0,
    fontSize: "0.63rem",
  },
};

function SquareDialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  maxWidth = "md",
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          minHeight: {
            xs: "auto",
            md: 560,
          },
          boxShadow:
            "0 24px 70px rgba(15, 23, 42, 0.18)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.2,
          py: 1.7,
          borderBottom:
            "1px solid #E3E7EC",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.92rem",
            fontWeight: 850,
          }}
        >
          {title}
        </Typography>

        {subtitle ? (
          <Typography
            sx={{
              mt: 0.25,
              color: "#667085",
              fontSize: "0.67rem",
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </DialogTitle>

      <DialogContent
        sx={{
          p: 2.2,
          bgcolor: "#FBFCFD",
        }}
      >
        {children}
      </DialogContent>

      {actions ? (
        <DialogActions
          sx={{
            px: 2.2,
            py: 1.4,
            borderTop:
              "1px solid #E3E7EC",
          }}
        >
          {actions}
        </DialogActions>
      ) : null}
    </Dialog>
  );
}

function UserForm({
  user,
  roles,
  shops,
  shopsLoading,
  shopsError,
  onCancel,
  onSubmit,
  busy,
}) {
  const {
    control,
    handleSubmit,
    formState: {
      errors,
    },
    watch,
  } = useForm({
    defaultValues: user
      ? {
          name: user.name || "",
          email: user.email || "",
          password: "",
          password_confirmation: "",
          role_id:
            user.role_id ||
            user.role?.id ||
            "",
          shop_ids: (
            user.shops || []
          ).map((shop) => shop.id),
          is_active:
            user.is_active ?? true,
        }
      : {
          name: "",
          email: "",
          password: "",
          password_confirmation: "",
          role_id: "",
          shop_ids: [],
          is_active: true,
        },
  });

  const password = watch("password");

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "220px 1fr",
          },
          gap: 2.2,
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 0,
            borderColor: "#E3E7EC",
            textAlign: "center",
          }}
        >
          <Avatar
            sx={{
              width: 92,
              height: 92,
              mx: "auto",
              bgcolor: "#EEF3FF",
              color: "#2F5BEA",
              fontSize: "1.5rem",
              fontWeight: 850,
            }}
          >
            {initials(
              user?.name || "New User",
            )}
          </Avatar>

          <Typography
            sx={{
              mt: 1.4,
              fontSize: "0.78rem",
              fontWeight: 800,
            }}
          >
            {user
              ? "User profile"
              : "New account"}
          </Typography>

          <Typography
            sx={{
              mt: 0.35,
              color: "#7A8696",
              fontSize: "0.64rem",
              lineHeight: 1.5,
            }}
          >
            Configure account details,
            role and shop access.
          </Typography>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 0,
            borderColor: "#E3E7EC",
          }}
        >
          <Typography
            sx={{
              mb: 1.3,
              fontSize: "0.7rem",
              fontWeight: 850,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Account information
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "1fr 1fr",
              },
              gap: 1.2,
            }}
          >
            <Controller
              name="name"
              control={control}
              rules={{
                required:
                  "Full name is required.",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Full name"
                  fullWidth
                  error={Boolean(
                    errors.name,
                  )}
                  helperText={
                    errors.name?.message
                  }
                  sx={fieldSx}
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              rules={{
                required:
                  "Email address is required.",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email address"
                  type="email"
                  fullWidth
                  error={Boolean(
                    errors.email,
                  )}
                  helperText={
                    errors.email?.message
                  }
                  sx={fieldSx}
                />
              )}
            />

            <Controller
              name="role_id"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Role"
                  fullWidth
                  sx={fieldSx}
                >
                  <MenuItem value="">
                    <em>No role</em>
                  </MenuItem>

                  {roles.map((role) => (
                    <MenuItem
                      key={role.id}
                      value={role.id}
                    >
                      {role.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="shop_ids"
              control={control}
              render={({ field }) => (
                <FormControl
                  fullWidth
                  error={Boolean(
                    shopsError,
                  )}
                  sx={fieldSx}
                >
                  <InputLabel id="shops-label">
                    Shop access
                  </InputLabel>

                  <Select
                    {...field}
                    value={
                      Array.isArray(
                        field.value,
                      )
                        ? field.value
                        : []
                    }
                    labelId="shops-label"
                    label="Shop access"
                    multiple
                    disabled={shopsLoading}
                    renderValue={(selected) => {
                      if (
                        !selected.length
                      ) {
                        return "No shops selected";
                      }

                      const selectedShops =
                        shops.filter(
                          (shop) =>
                            selected.includes(
                              shop.id,
                            ),
                        );

                      if (
                        selectedShops.length <=
                        2
                      ) {
                        return selectedShops
                          .map(
                            (shop) =>
                              shop.shop_name,
                          )
                          .join(", ");
                      }

                      return `${selected.length} shops selected`;
                    }}
                    onChange={(event) => {
                      const value =
                        event.target.value;

                      field.onChange(
                        typeof value ===
                          "string"
                          ? value.split(",")
                          : value,
                      );
                    }}
                  >
                    {shops.map((shop) => {
                      const selected =
                        field.value.includes(
                          shop.id,
                        );

                      return (
                        <MenuItem
                          key={shop.id}
                          value={shop.id}
                        >
                          <Checkbox
                            size="small"
                            checked={selected}
                          />

                          <ListItemText
                            primary={
                              shop.shop_name
                            }
                            secondary={`${shop.shop_code} · ${
                              shop.brand
                                ?.name ||
                              shop.brand ||
                              "No brand"
                            }`}
                            primaryTypographyProps={{
                              fontSize:
                                "0.7rem",
                              fontWeight:
                                700,
                            }}
                            secondaryTypographyProps={{
                              fontSize:
                                "0.62rem",
                            }}
                          />
                        </MenuItem>
                      );
                    })}
                  </Select>

                  {shopsLoading ? (
                    <FormHelperText>
                      Loading shops from
                      the server...
                    </FormHelperText>
                  ) : shopsError ? (
                    <FormHelperText>
                      Unable to load shop
                      options.
                    </FormHelperText>
                  ) : !shops.length ? (
                    <FormHelperText>
                      No active shops are
                      available.
                    </FormHelperText>
                  ) : (
                    <FormHelperText>
                      Select one or more
                      shops for this user.
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="password"
              control={control}
              rules={{
                validate: (value) => {
                  if (
                    user &&
                    !value
                  ) {
                    return true;
                  }

                  return (
                    value.length >= 8 ||
                    "Use at least 8 characters."
                  );
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={
                    user
                      ? "New password"
                      : "Password"
                  }
                  type="password"
                  fullWidth
                  error={Boolean(
                    errors.password,
                  )}
                  helperText={
                    errors.password
                      ?.message ||
                    (user
                      ? "Leave blank to keep the current password."
                      : "")
                  }
                  sx={fieldSx}
                />
              )}
            />

            <Controller
              name="password_confirmation"
              control={control}
              rules={{
                validate: (value) => {
                  if (
                    user &&
                    !password &&
                    !value
                  ) {
                    return true;
                  }

                  return (
                    value === password ||
                    "Passwords do not match."
                  );
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Confirm password"
                  type="password"
                  fullWidth
                  error={Boolean(
                    errors
                      .password_confirmation,
                  )}
                  helperText={
                    errors
                      .password_confirmation
                      ?.message
                  }
                  sx={fieldSx}
                />
              )}
            />
          </Box>

          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ mt: 1.2 }}
                control={
                  <Switch
                    size="small"
                    checked={Boolean(
                      field.value,
                    )}
                    onChange={(
                      _,
                      checked,
                    ) =>
                      field.onChange(
                        checked,
                      )
                    }
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize:
                        "0.7rem",
                      fontWeight: 700,
                    }}
                  >
                    Active account
                  </Typography>
                }
              />
            )}
          />
        </Paper>
      </Box>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={{ mt: 2 }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onCancel}
          disabled={busy}
          sx={{
            borderRadius: 0,
            fontSize: "0.7rem",
          }}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          variant="contained"
          disabled={busy}
          sx={{
            minWidth: 130,
            borderRadius: 0,
            fontSize: "0.7rem",
            fontWeight: 800,
          }}
        >
          {busy
            ? "Saving..."
            : user
              ? "Save changes"
              : "Create user"}
        </Button>
      </Stack>
    </Box>
  );
}

function RowMenu({
  row,
  onView,
  onEdit,
  onAdmin,
  onDelete,
}) {
  const [anchor, setAnchor] =
    useState(null);

  const choose = (handler) => {
    setAnchor(null);
    handler(row);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={(event) =>
          setAnchor(
            event.currentTarget,
          )
        }
      >
        <MoreVertRoundedIcon
          sx={{ fontSize: 17 }}
        />
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() =>
          setAnchor(null)
        }
        PaperProps={{
          sx: {
            minWidth: 180,
            borderRadius: 0,
          },
        }}
      >
        <MenuItem
          onClick={() =>
            choose(onView)
          }
          sx={{
            gap: 1,
            fontSize: "0.7rem",
          }}
        >
          <PersonOutlineRoundedIcon
            sx={{ fontSize: 16 }}
          />

          View details
        </MenuItem>

        <MenuItem
          onClick={() =>
            choose(onEdit)
          }
          sx={{
            gap: 1,
            fontSize: "0.7rem",
          }}
        >
          <EditOutlinedIcon
            sx={{ fontSize: 16 }}
          />

          Edit user
        </MenuItem>

        <MenuItem
          onClick={() =>
            choose(onAdmin)
          }
          sx={{
            gap: 1,
            fontSize: "0.7rem",
          }}
        >
          <AdminPanelSettingsOutlinedIcon
            sx={{ fontSize: 16 }}
          />

          Manage access
        </MenuItem>

        <MenuItem
          onClick={() =>
            choose(onDelete)
          }
          sx={{
            gap: 1,
            color: "error.main",
            fontSize: "0.7rem",
          }}
        >
          <DeleteOutlineRoundedIcon
            sx={{ fontSize: 16 }}
          />

          Delete user
        </MenuItem>
      </Menu>
    </>
  );
}

export function UsersPage() {
  const queryClient =
    useQueryClient();

  const [search, setSearch] =
    useState("");

  const [dialog, setDialog] =
    useState({
      type: null,
      user: null,
    });

  const [
    adminTarget,
    setAdminTarget,
  ] = useState(null);

  const [message, setMessage] =
    useState(null);

  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: getUsers,
  });

  const rolesQuery = useQuery({
    queryKey: ["roles"],
    queryFn: getRoles,
  });

  const shopsQuery = useQuery({
    queryKey: [
      "shop-options",
    ],
    queryFn: () =>
      getShops({
        page: 1,
        per_page: 100,
        is_active: 1,
      }),
    staleTime:
      5 * 60 * 1000,
  });

  const users = useMemo(
    () =>
      extractUsers(
        usersQuery.data,
      ),
    [usersQuery.data],
  );

  const roles = useMemo(
    () =>
      extractRoles(
        rolesQuery.data,
      ),
    [rolesQuery.data],
  );

  const shopOptions =
    useMemo(
      () =>
        extractShops(
          shopsQuery.data,
        ),
      [shopsQuery.data],
    );

  const rows = useMemo(() => {
    const term = search
      .trim()
      .toLowerCase();

    return users.filter((user) => {
      if (!term) {
        return true;
      }

      return [
        user.name,
        user.email,
        user.role?.name,
        ...(user.shops || []).map(
          (shop) =>
            shop.shop_name,
        ),
      ]
        .filter(Boolean)
        .some((value) =>
          String(value)
            .toLowerCase()
            .includes(term),
        );
    });
  }, [search, users]);

const saveMutation = useMutation({
  mutationFn: async ({
    user,
    values,
  }) => {
    /*
     * EDIT EXISTING USER
     */
    if (user) {
      const payload = {
        name: values.name,
        email: values.email,
        is_active: values.is_active,
      };

      if (values.password) {
        payload.password =
          values.password;

        payload.password_confirmation =
          values.password_confirmation;
      }

      const response =
        await updateUser(
          user.id,
          payload,
        );

      /*
       * Existing user access is
       * updated separately.
       */
      await assignUserRole(
        user.id,
        values.role_id,
      );

      await assignUserShops(
        user.id,
        values.shop_ids || [],
      );

      return response;
    }

    /*
     * CREATE NEW USER
     *
     * Backend register endpoint
     * requires role_id and shop_ids.
     */
    const payload = {
      name: values.name,
      email: values.email,

      password:
        values.password,

      password_confirmation:
        values.password_confirmation,

      role_id:
        values.role_id,

      shop_ids:
        values.shop_ids || [],

      is_active:
        values.is_active,
    };

    return createUser(payload);
  },

  onSuccess: (
    _,
    variables,
  ) => {
    queryClient.invalidateQueries({
      queryKey: ["users"],
    });

    queryClient.invalidateQueries({
      queryKey: [
        "shop-options",
      ],
    });

    setDialog({
      type: null,
      user: null,
    });

    setMessage({
      title: variables.user
        ? "User updated"
        : "User created",

      text: variables.user
        ? "The user account was updated successfully."
        : "The new user account was created successfully.",
    });
  },

  onError: (error) => {
    setMessage({
      title:
        "Unable to save user",

      text:
        getApiErrorMessage(
          error,
        ),
    });
  },
});
  const deleteMutation =
    useMutation({
      mutationFn: (userId) =>
        deleteUser(userId),

      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["users"],
        });

        setDialog({
          type: null,
          user: null,
        });

        setMessage({
          title: "User deleted",
          text:
            "The user was deleted successfully.",
        });
      },

      onError: (error) => {
        setMessage({
          title:
            "Unable to delete user",
          text:
            getApiErrorMessage(
              error,
            ),
        });
      },
    });

  const columns = [
    {
      field: "avatar",
      headerName: "",
      width: 54,
      sortable: false,
      filterable: false,
      renderCell: (
        params,
      ) => (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: "#EEF3FF",
            color: "#2F5BEA",
            fontSize: "0.62rem",
            fontWeight: 850,
          }}
        >
          {initials(
            params.row.name,
          )}
        </Avatar>
      ),
    },

    {
      field: "name",
      headerName: "Full name",
      minWidth: 180,
      flex: 0.9,
      renderCell: (
        params,
      ) => (
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 800,
          }}
        >
          {params.row.name}
        </Typography>
      ),
    },

    {
      field: "email",
      headerName:
        "Email address",
      minWidth: 230,
      flex: 1.2,
    },

    {
      field: "role",
      headerName: "Role",
      minWidth: 150,
      flex: 0.75,
      valueGetter: (
        _,
        row,
      ) =>
        row.role?.name ||
        "Unassigned",
    },

    {
      field: "shops",
      headerName:
        "Shop access",
      minWidth: 150,
      flex: 0.75,
      sortable: false,
      renderCell: (
        params,
      ) => {
        const count =
          params.row.shops
            ?.length || 0;

        return (
          <Stack
            direction="row"
            spacing={0.6}
          >
            <Typography
              sx={{
                fontSize:
                  "0.7rem",
                fontWeight: 700,
              }}
            >
              {count} shop
              {count === 1
                ? ""
                : "s"}
            </Typography>

            {count > 0 ? (
              <Button
                size="small"
                variant="text"
                onClick={() =>
                  setDialog({
                    type: "view",
                    user:
                      params.row,
                  })
                }
                sx={{
                  minWidth:
                    "auto",
                  p: 0,
                  fontSize:
                    "0.66rem",
                }}
              >
                View
              </Button>
            ) : null}
          </Stack>
        );
      },
    },

    {
      field: "status",
      headerName: "Status",
      width: 90,
      renderCell: (
        params,
      ) => {
        const active =
          params.row
            .is_active ??
          true;

        return (
          <Chip
            label={
              active
                ? "Active"
                : "Inactive"
            }
            size="small"
            sx={{
              height: 22,
              borderRadius: 0,
              bgcolor: active
                ? "#E7F6EE"
                : "#F1F3F5",
              color: active
                ? "#18794E"
                : "#667085",
              fontSize:
                "0.65rem",
              fontWeight: 750,
            }}
          />
        );
      },
    },

    {
      field: "created_at",
      headerName: "Created",
      minWidth: 130,
      valueFormatter: (
        value,
      ) =>
        value
          ? new Date(
              value,
            ).toLocaleDateString(
              "en-ZA",
            )
          : "—",
    },

    {
      field: "actions",
      headerName: "",
      width: 54,
      sortable: false,
      filterable: false,
      renderCell: (
        params,
      ) => (
        <RowMenu
          row={params.row}
          onView={(user) =>
            setDialog({
              type: "view",
              user,
            })
          }
          onEdit={(user) =>
            setDialog({
              type: "edit",
              user,
            })
          }
          onAdmin={(user) =>
            setAdminTarget(
              user,
            )
          }
          onDelete={(user) =>
            setDialog({
              type: "delete",
              user,
            })
          }
        />
      ),
    },
  ];

  const closeDialog = () => {
    if (
      !saveMutation.isPending &&
      !deleteMutation.isPending
    ) {
      setDialog({
        type: null,
        user: null,
      });
    }
  };

  return (
    <Box
      sx={{
        p: {
          xs: 1.2,
          md: 2,
        },
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 0,
          borderColor:
            "#DDE3EA",
          boxShadow: "none",
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          spacing={1}
          sx={{
            p: 1.2,
            borderBottom:
              "1px solid #E3E7EC",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize:
                  "0.82rem",
                fontWeight: 850,
              }}
            >
              Users
            </Typography>

            <Typography
              sx={{
                mt: 0.25,
                color: "#667085",
                fontSize:
                  "0.66rem",
              }}
            >
              Manage portal users,
              roles and shop access.
            </Typography>
          </Box>

          <TextField
            size="small"
            value={search}
            onChange={(event) =>
              setSearch(
                event.target.value,
              )
            }
            placeholder="Search users"
            sx={{
              width: {
                xs: "100%",
                md: 270,
              },

              "& .MuiInputBase-root":
                {
                  height: 36,
                  borderRadius: 0,
                  fontSize:
                    "0.7rem",
                },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon
                      sx={{
                        fontSize: 16,
                      }}
                    />
                  </InputAdornment>
                ),
              },
            }}
          />

          <Button
            variant="contained"
            startIcon={
              <AddRoundedIcon
                sx={{
                  fontSize: 16,
                }}
              />
            }
            onClick={() =>
              setDialog({
                type: "create",
                user: null,
              })
            }
            sx={{
              minHeight: 36,
              borderRadius: 0,
              fontSize:
                "0.7rem",
              fontWeight: 800,
            }}
          >
            New user
          </Button>
        </Stack>

        <SoftDataGrid
          rows={rows}
          columns={columns}
          loading={
            usersQuery.isLoading
          }
          checkboxSelection
          getRowId={(row) =>
            row.id
          }
        />
      </Paper>

      <SquareDialog
        open={
          dialog.type ===
            "create" ||
          dialog.type === "edit"
        }
        onClose={closeDialog}
        title={
          dialog.type === "edit"
            ? "Edit user"
            : "Create user"
        }
        subtitle="Manage account details, role assignment and shop access."
      >
        <UserForm
          key={`${dialog.type}-${
            dialog.user?.id ||
            "new"
          }`}
          user={
            dialog.type === "edit"
              ? dialog.user
              : null
          }
          roles={roles}
          shops={shopOptions}
          shopsLoading={
            shopsQuery.isLoading
          }
          shopsError={
            shopsQuery.isError
          }
          busy={
            saveMutation.isPending
          }
          onCancel={closeDialog}
          onSubmit={(values) =>
            saveMutation.mutate({
              user:
                dialog.type ===
                "edit"
                  ? dialog.user
                  : null,
              values,
            })
          }
        />
      </SquareDialog>

      <SquareDialog
        open={
          dialog.type === "view"
        }
        onClose={closeDialog}
        title="User details"
        subtitle="Account, role and assigned shops."
        actions={
          <>
            <Button
              color="inherit"
              onClick={closeDialog}
              sx={{
                borderRadius: 0,
                fontSize:
                  "0.7rem",
              }}
            >
              Close
            </Button>

            <Button
              variant="contained"
              onClick={() =>
                setDialog({
                  type: "edit",
                  user:
                    dialog.user,
                })
              }
              sx={{
                borderRadius: 0,
                fontSize:
                  "0.7rem",
              }}
            >
              Edit user
            </Button>
          </>
        }
      >
        {dialog.user ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                {
                  xs: "1fr",
                  md: "220px 1fr",
                },
              gap: 2.2,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 0,
                borderColor:
                  "#E3E7EC",
                textAlign:
                  "center",
              }}
            >
              <Avatar
                sx={{
                  width: 104,
                  height: 104,
                  mx: "auto",
                  bgcolor:
                    "#EEF3FF",
                  color:
                    "#2F5BEA",
                  fontSize:
                    "1.7rem",
                  fontWeight: 850,
                }}
              >
                {initials(
                  dialog.user
                    .name,
                )}
              </Avatar>

              <Typography
                sx={{
                  mt: 1.5,
                  fontSize:
                    "0.84rem",
                  fontWeight: 850,
                }}
              >
                {dialog.user.name}
              </Typography>

              <Typography
                sx={{
                  mt: 0.3,
                  color:
                    "#667085",
                  fontSize:
                    "0.67rem",
                }}
              >
                {dialog.user.email}
              </Typography>

              <Chip
                label={
                  dialog.user.role
                    ?.name ||
                  "Unassigned"
                }
                size="small"
                sx={{
                  mt: 1,
                  borderRadius: 0,
                  fontSize:
                    "0.64rem",
                }}
              />
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 1.7,
                borderRadius: 0,
                borderColor:
                  "#E3E7EC",
              }}
            >
              <Typography
                sx={{
                  mb: 1,
                  fontSize:
                    "0.68rem",
                  fontWeight: 850,
                  textTransform:
                    "uppercase",
                  letterSpacing:
                    "0.08em",
                }}
              >
                Shop access
              </Typography>

              {(
                dialog.user.shops ||
                []
              ).length ? (
                (
                  dialog.user
                    .shops || []
                ).map((shop) => (
                  <Box
                    key={shop.id}
                    sx={{
                      p: 1,
                      borderBottom:
                        "1px solid #EDF0F3",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize:
                          "0.7rem",
                        fontWeight:
                          800,
                      }}
                    >
                      {shop.shop_name}
                    </Typography>

                    <Typography
                      sx={{
                        color:
                          "#667085",
                        fontSize:
                          "0.63rem",
                      }}
                    >
                      {shop.shop_code}
                      {" · "}
                      {shop.brand
                        ?.name ||
                        shop.brand ||
                        "No brand"}
                      {" · "}
                      {shop.location ||
                        "No location"}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography
                  sx={{
                    fontSize:
                      "0.68rem",
                    color:
                      "#667085",
                  }}
                >
                  No shops assigned.
                </Typography>
              )}
            </Paper>
          </Box>
        ) : null}
      </SquareDialog>

      <SquareDialog
        open={
          dialog.type ===
          "delete"
        }
        onClose={closeDialog}
        title="Delete user"
        subtitle="This action cannot be undone."
        maxWidth="sm"
        actions={
          <>
            <Button
              color="inherit"
              onClick={closeDialog}
              sx={{
                borderRadius: 0,
                fontSize:
                  "0.7rem",
              }}
            >
              Cancel
            </Button>

            <Button
              color="error"
              variant="contained"
              disabled={
                deleteMutation.isPending
              }
              onClick={() =>
                deleteMutation.mutate(
                  dialog.user.id,
                )
              }
              sx={{
                borderRadius: 0,
                fontSize:
                  "0.7rem",
                fontWeight: 800,
              }}
            >
              {deleteMutation.isPending
                ? "Deleting..."
                : "Delete user"}
            </Button>
          </>
        }
      >
        <Stack
          alignItems="center"
          sx={{ py: 3 }}
        >
          <Avatar
            sx={{
              width: 88,
              height: 88,
              bgcolor:
                "#FFF1F1",
              color: "#D92D20",
              fontSize:
                "1.4rem",
              fontWeight: 850,
            }}
          >
            {initials(
              dialog.user?.name,
            )}
          </Avatar>

          <Typography
            sx={{
              mt: 1.5,
              fontSize:
                "0.88rem",
              fontWeight: 850,
            }}
          >
            Delete{" "}
            {dialog.user?.name}?
          </Typography>

          <Typography
            sx={{
              mt: 0.65,
              color: "#667085",
              fontSize:
                "0.68rem",
              textAlign:
                "center",
            }}
          >
            The account, role and
            shop access will be
            removed.
          </Typography>
        </Stack>
      </SquareDialog>

      {adminTarget ? (
        <UserAdminActions
          open={Boolean(
            adminTarget,
          )}
          onClose={() =>
            setAdminTarget(null)
          }
          user={adminTarget}
          roles={roles}
          shops={shopOptions}
          shopsLoading={
            shopsQuery.isLoading
          }
        />
      ) : null}

      <Dialog
        open={Boolean(message)}
        onClose={() =>
          setMessage(null)
        }
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "0.82rem",
            fontWeight: 850,
          }}
        >
          {message?.title}
        </DialogTitle>

        <DialogContent dividers>
          <Typography
            sx={{
              fontSize: "0.7rem",
              lineHeight: 1.6,
            }}
          >
            {message?.text}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            onClick={() =>
              setMessage(null)
            }
            sx={{
              borderRadius: 0,
              fontSize: "0.7rem",
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}